import type { JSONSchemaType } from 'ajv'
import type { ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'

// Board Card Slices
export const BOARD_CARD_OPEN_ACTION_ID = 'board.card.open'
export const BOARD_CARD_MOVE_ACTION_ID = 'board.card.move'
export const BOARD_CARD_DUPLICATE_ACTION_ID = 'board.card.duplicate'
export const BOARD_CARD_DELETE_ACTION_ID = 'board.card.delete'

// Board Column Header Slices
export const BOARD_COLUMN_RENAME_ACTION_ID = 'board.column.rename'
export const BOARD_COLUMN_REORDER_ACTION_ID = 'board.column.reorder'
export const BOARD_COLUMN_DELETE_ACTION_ID = 'board.column.delete'

// Board Cell Slices
export const BOARD_CELL_NEW_ACTION_ID = 'board.cell.new'
export const BOARD_CELL_PASTE_ACTION_ID = 'board.cell.paste'
export const BOARD_CELL_SELECT_ACTION_ID = 'board.cell.select'

// Board Canvas Slices
export const BOARD_CANVAS_NEW_CARD_ACTION_ID = 'board.canvas.new-card'
export const BOARD_CANVAS_NEW_COLUMN_ACTION_ID = 'board.canvas.new-column'

export type BoardOrbitTargetArgs = {
  id?: string
  title?: string
}

const boardOrbitTargetArgsSchema: JSONSchemaType<BoardOrbitTargetArgs> = {
  type: 'object',
  properties: {
    id: { type: 'string', nullable: true },
    title: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: true,
}

const TARGET_REQUIRED_ACTION_IDS = new Set([
  BOARD_CARD_OPEN_ACTION_ID,
  BOARD_CARD_MOVE_ACTION_ID,
  BOARD_CARD_DUPLICATE_ACTION_ID,
  BOARD_CARD_DELETE_ACTION_ID,
  BOARD_COLUMN_RENAME_ACTION_ID,
  BOARD_COLUMN_REORDER_ACTION_ID,
  BOARD_COLUMN_DELETE_ACTION_ID,
  BOARD_CELL_NEW_ACTION_ID,
  BOARD_CELL_PASTE_ACTION_ID,
  BOARD_CELL_SELECT_ACTION_ID,
])

const makeOrbitBoardAction = (
  id: string,
  label: string,
  description: string,
  risk: 'READ_ONLY' | 'LOW' | 'MEDIUM' | 'HIGH',
  permissionAction?: 'board.edit' | 'board.move_items',
): ActionDefinition<BoardOrbitTargetArgs> => ({
  id,
  label,
  description,
  risk,
  domain: 'board',
  surfaces: ['orbit', 'catalog'],
  argsSchema: boardOrbitTargetArgsSchema,
  permission: permissionAction ? (context) => context.canPerform(permissionAction) : undefined,
  run: async (args, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'orbit-correlation'

    if (TARGET_REQUIRED_ACTION_IDS.has(id) && !args.id) {
      const message = `Target ID is required for ${label}`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    const ops = context.boardOps
    let handler: (() => Promise<void> | void) | undefined

    if (id === BOARD_CARD_OPEN_ACTION_ID) {
      handler = ops?.openCard ? () => ops.openCard!(args.id!) : undefined
    } else if (id === BOARD_CARD_MOVE_ACTION_ID) {
      handler = ops?.moveCard ? () => ops.moveCard!(args.id!) : undefined
    } else if (id === BOARD_CARD_DUPLICATE_ACTION_ID) {
      handler = ops?.duplicateCard ? () => ops.duplicateCard!(args.id!) : undefined
    } else if (id === BOARD_CARD_DELETE_ACTION_ID) {
      handler = ops?.deleteCard ? () => ops.deleteCard!(args.id!) : undefined
    } else if (id === BOARD_COLUMN_RENAME_ACTION_ID) {
      handler = ops?.renameColumn ? () => ops.renameColumn!(args.id!) : undefined
    } else if (id === BOARD_COLUMN_REORDER_ACTION_ID) {
      handler = ops?.reorderColumn ? () => ops.reorderColumn!(args.id!) : undefined
    } else if (id === BOARD_COLUMN_DELETE_ACTION_ID) {
      handler = ops?.deleteColumn ? () => ops.deleteColumn!(args.id!) : undefined
    } else if (id === BOARD_CELL_NEW_ACTION_ID) {
      handler = ops?.newCardInCell ? () => ops.newCardInCell!(args.id!) : undefined
    } else if (id === BOARD_CELL_PASTE_ACTION_ID) {
      handler = ops?.pasteIntoCell ? () => ops.pasteIntoCell!(args.id!) : undefined
    } else if (id === BOARD_CELL_SELECT_ACTION_ID) {
      handler = ops?.selectColumnFromCell ? () => ops.selectColumnFromCell!(args.id!) : undefined
    } else if (id === BOARD_CANVAS_NEW_CARD_ACTION_ID) {
      handler = ops?.createCard ? () => ops.createCard!() : undefined
    } else if (id === BOARD_CANVAS_NEW_COLUMN_ACTION_ID) {
      handler = ops?.createColumn ? () => ops.createColumn!() : undefined
    }

    if (!handler) {
      const message = `Operation for ${label} (${id}) is not available`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    try {
      await Promise.resolve(handler())
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to execute ${label}`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    context.showToast?.(`${label}${args.title ? ` · ${args.title}` : ''}`, 'info')
    return {
      kind: 'COMPLETED',
      result: { id: args.id, title: args.title },
      message: `${label} dispatched`,
      correlationId,
    }
  },
})

export const boardCardOpenAction = makeOrbitBoardAction(
  BOARD_CARD_OPEN_ACTION_ID,
  'Open card',
  'Opens board card details.',
  'READ_ONLY',
)

export const boardCardMoveAction = makeOrbitBoardAction(
  BOARD_CARD_MOVE_ACTION_ID,
  'Move column',
  'Moves board card to another column.',
  'LOW',
  'board.move_items',
)

export const boardCardDuplicateAction = makeOrbitBoardAction(
  BOARD_CARD_DUPLICATE_ACTION_ID,
  'Duplicate card',
  'Duplicates a board card.',
  'LOW',
  'board.edit',
)

export const boardCardDeleteAction = makeOrbitBoardAction(
  BOARD_CARD_DELETE_ACTION_ID,
  'Delete card',
  'Deletes a board card.',
  'HIGH',
  'board.edit',
)

export const boardColumnRenameAction = makeOrbitBoardAction(
  BOARD_COLUMN_RENAME_ACTION_ID,
  'Rename column',
  'Renames a board column header.',
  'LOW',
  'board.edit',
)

export const boardColumnReorderAction = makeOrbitBoardAction(
  BOARD_COLUMN_REORDER_ACTION_ID,
  'Reorder column',
  'Reorders a board column.',
  'LOW',
  'board.edit',
)

export const boardColumnDeleteAction = makeOrbitBoardAction(
  BOARD_COLUMN_DELETE_ACTION_ID,
  'Delete column',
  'Deletes a board column.',
  'HIGH',
  'board.edit',
)

export const boardCellNewAction = makeOrbitBoardAction(
  BOARD_CELL_NEW_ACTION_ID,
  'New card here',
  'Creates a new card in the selected cell.',
  'LOW',
  'board.edit',
)

export const boardCellPasteAction = makeOrbitBoardAction(
  BOARD_CELL_PASTE_ACTION_ID,
  'Paste',
  'Pastes clipboard card into the selected cell.',
  'LOW',
  'board.edit',
)

export const boardCellSelectAction = makeOrbitBoardAction(
  BOARD_CELL_SELECT_ACTION_ID,
  'Select column',
  'Selects the column from the active cell.',
  'READ_ONLY',
)

export const boardCanvasNewCardAction = makeOrbitBoardAction(
  BOARD_CANVAS_NEW_CARD_ACTION_ID,
  'New card',
  'Creates a new card on the board canvas.',
  'LOW',
  'board.edit',
)

export const boardCanvasNewColumnAction = makeOrbitBoardAction(
  BOARD_CANVAS_NEW_COLUMN_ACTION_ID,
  'New column',
  'Creates a new column on the board canvas.',
  'LOW',
  'board.edit',
)

export const registerBoardActions = (): void => {
  if (!getAction(BOARD_CARD_OPEN_ACTION_ID)) registerAction(boardCardOpenAction)
  if (!getAction(BOARD_CARD_MOVE_ACTION_ID)) registerAction(boardCardMoveAction)
  if (!getAction(BOARD_CARD_DUPLICATE_ACTION_ID)) registerAction(boardCardDuplicateAction)
  if (!getAction(BOARD_CARD_DELETE_ACTION_ID)) registerAction(boardCardDeleteAction)
  if (!getAction(BOARD_COLUMN_RENAME_ACTION_ID)) registerAction(boardColumnRenameAction)
  if (!getAction(BOARD_COLUMN_REORDER_ACTION_ID)) registerAction(boardColumnReorderAction)
  if (!getAction(BOARD_COLUMN_DELETE_ACTION_ID)) registerAction(boardColumnDeleteAction)
  if (!getAction(BOARD_CELL_NEW_ACTION_ID)) registerAction(boardCellNewAction)
  if (!getAction(BOARD_CELL_PASTE_ACTION_ID)) registerAction(boardCellPasteAction)
  if (!getAction(BOARD_CELL_SELECT_ACTION_ID)) registerAction(boardCellSelectAction)
  if (!getAction(BOARD_CANVAS_NEW_CARD_ACTION_ID)) registerAction(boardCanvasNewCardAction)
  if (!getAction(BOARD_CANVAS_NEW_COLUMN_ACTION_ID)) registerAction(boardCanvasNewColumnAction)
}

registerBoardActions()
